import { Chat, ChatHeader, ChatMessageInputBar, ChatPageSkeletonLayout, ContactInfoCard } from '@if/mui'
import type { ChatMessageInputBarHandle, MessageVariant } from '@if/mui'
import { ECreditApplicationStatus, ELanguage, EMessageType } from '@if/core/types'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import { Box, Container, Stack, ToggleButton, Typography, useTheme } from '@mui/material'
import { blue, blueGrey, grey } from '@mui/material/colors'
import { Constants } from '@src/types/Constants'
import { useTranslation } from 'react-i18next'
import {
  useDeleteMessage,
  useEditMessage,
  useGetConversation,
  useMarkConversationAsRead,
  useMarkMessageRead,
  useMarkMessageUnread,
  useSendMessage,
} from '@src/data/api/chat/chat-api'
import type { ChatMessage } from '@if/core/types'
import { useUser } from '@src/contexts/AppContext'
import useFeatureFlags from '@src/hooks/config-hooks'
import {
  applyChatMessageEvent,
  getSignalRConnection,
  useSignalRConnectionReady,
  type MessageUpdatedEvent,
  MessageUpdateType,
} from '@src/hooks/useChatMessageSignalR'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

const CURRENT_USER_PARTICIPANT_TYPE = EMessageType.External

const ChatPage = () => {
  const { flags, isFetchingFeatureSwitch } = useFeatureFlags()
  const { creditApplicationId, financingProgramId } = useParams<{
    creditApplicationId: string
    financingProgramId: string
  }>()
  const navigate = useNavigate()
  const user = useUser()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { t: tGlobal } = useTranslation('global')
  const { t } = useTranslation()
  const [formatMode, setFormatMode] = useState(true)

  const getMessageVariant = useCallback(
    (message: ChatMessage): MessageVariant => {
      if (message.sentById === Constants.SystemUserId) return 'neutral'
      if (message.sentById === user?.id) return 'sent'
      return 'received'
    },
    [user?.id],
  )

  const chatPalette = useMemo(
    () => ({
      background: isDark ? grey[900] : grey[100],
      messageSent: blue[700],
      messageSentText: '#FFFFFF',
      messageReceived: isDark ? blueGrey[800] : blue[50],
      messageReceivedText: isDark ? grey[100] : grey[900],
      avatarSent: isDark ? blue[500] : blue[700],
      avatarReceived: isDark ? blueGrey[600] : blue[200],
      isCancelled: isDark ? grey[600] : grey[300],
    }),
    [isDark],
  )

  const messageInputRef = useRef<ChatMessageInputBarHandle>(null)

  // Data fetching
  const [conversation, isLoading] = useGetConversation(creditApplicationId, financingProgramId)

  const isCancelled =
    conversation?.applicationStatus === ECreditApplicationStatus.Cancelled ||
    conversation?.applicationStatus === ECreditApplicationStatus.Expired

  // Message mutations
  const [sendMsg] = useSendMessage()
  const [deleteMsg] = useDeleteMessage()
  const [editMsg] = useEditMessage()
  const [markUnread] = useMarkMessageUnread()
  const [markAsRead] = useMarkMessageRead()
  const [markConversationAsRead] = useMarkConversationAsRead()

  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([])

  // SignalR real-time message updates
  const signalRReady = useSignalRConnectionReady()
  const markAsReadRef = useRef(markAsRead)
  markAsReadRef.current = markAsRead

  useEffect(() => {
    const connection = getSignalRConnection()
    if (!connection) return

    const handleMessageUpdated = (event: MessageUpdatedEvent) => {
      if (event.creditApplicationId !== creditApplicationId) return

      setLocalMessages((prev) => applyChatMessageEvent(prev, event))

      if (event.updateType === MessageUpdateType.Created && event.updatedById && event.updatedById !== user?.id) {
        void markAsReadRef.current({ creditApplicationId: creditApplicationId ?? '', messageId: event.id })
      }
    }

    connection.on('messageUpdated', handleMessageUpdated)

    return () => {
      connection.off('messageUpdated', handleMessageUpdated)
    }
  }, [creditApplicationId, user?.id, signalRReady])

  // Initialize local messages from server data
  useEffect(() => {
    if (conversation?.messages) {
      setLocalMessages(conversation.messages)

      if (creditApplicationId && conversation.messages.some((m) => !m.isRead && m.sentById !== user?.id)) {
        void markConversationAsRead({ creditApplicationId }).then((response) => {
          if (response?.messageCount > 0) {
            setLocalMessages((prev) =>
              prev.map((m) =>
                !m.isRead && m.sentById !== user?.id
                  ? { ...m, isRead: true, readAt: new Date(), readByName: user?.fullName }
                  : m,
              ),
            )
          }
        })
      }
    }
  }, [conversation?.messages, creditApplicationId, user?.id, user?.fullName, markConversationAsRead, setLocalMessages])

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (isCancelled || !creditApplicationId) return
      await deleteMsg({ creditApplicationId, messageId })
      setLocalMessages((prev) => prev.filter((m) => m.id !== messageId))
    },
    [creditApplicationId, deleteMsg, isCancelled, setLocalMessages],
  )

  const handleEditMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (isCancelled || !creditApplicationId) return
      await editMsg({ creditApplicationId, messageId, content: newContent })
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, content: newContent, editedAt: new Date() } : m)),
      )
    },
    [creditApplicationId, editMsg, isCancelled, setLocalMessages],
  )

  const handleMarkUnreadMessage = useCallback(
    async (messageId: string) => {
      if (isCancelled || !creditApplicationId) return
      await markUnread({ creditApplicationId, messageId })
      setLocalMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, isRead: false, readAt: undefined, markedUnreadById: user?.id } : m,
        ),
      )
    },
    [creditApplicationId, markUnread, user?.id, isCancelled, setLocalMessages],
  )

  const handleSendMessage = useCallback(
    async (messageContent: string) => {
      if (!creditApplicationId || !financingProgramId || !user) return
      await sendMsg({
        id: crypto.randomUUID(),
        content: messageContent,
        sentAt: new Date(),
        sentByName: user.fullName,
        sentById: user.id,
        merchantId: user.merchantId,
        creditApplicationId,
        financingProgramId,
        isRead: false,
        type: CURRENT_USER_PARTICIPANT_TYPE,
      })
    },
    [creditApplicationId, financingProgramId, user, sendMsg],
  )

  const handleBack = useCallback(() => {
    navigate('/chat-dashboard')
  }, [navigate])

  const language = useMemo(() => {
    const lang = conversation?.merchantLanguage
    return lang === ELanguage.French ? 'FR' : 'EN'
  }, [conversation?.merchantLanguage])

  if (!isFetchingFeatureSwitch && !flags.UseInstantMessage) return <Navigate to="/" replace />

  if (isLoading) {
    return <ChatPageSkeletonLayout />
  }

  return (
    <Container
      maxWidth="lg"
      sx={(theme) => ({
        py: 3,
        height: `calc(100dvh - ${(theme.mixins.toolbar.minHeight as number) ?? 64}px)`,
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      <ChatHeader
        province={conversation?.province}
        language={language}
        isAgent={false}
        currentUserId={user?.id ?? ''}
        onBack={handleBack}
      />

      <ContactInfoCard
        referenceNumber={conversation?.conversationNumber ?? ''}
        fullName={conversation?.fullName ?? ''}
        email={conversation?.email ?? ''}
        phone={conversation?.phone ?? ''}
        merchantName={conversation?.merchantName ?? ''}
        createdByName={conversation?.applicationCreatedByName ?? ''}
        isCancelled={isCancelled}
        onApplicationClick={() => {
          if (financingProgramId && creditApplicationId) {
            navigate(`/credits/${financingProgramId}/${creditApplicationId}`)
          }
        }}
        applicationHint={
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon sx={{ fontSize: '0.875rem' }} />
            <Typography variant="caption">{t('instantMessage.clickToGoToApplication')}</Typography>
          </Stack>
        }
      />

      <Box sx={{ flex: 1, overflow: 'hidden', mb: 2 }}>
        <Chat
          messages={localMessages}
          systemMessage={conversation?.systemMessage}
          currentUserId={user?.id ?? ''}
          isAgent={false}
          onDeleteMessage={handleDeleteMessage}
          onEditMessage={handleEditMessage}
          onMarkUnreadMessage={handleMarkUnreadMessage}
          isLockedByOther={false}
          isCancelled={isCancelled}
          chatPalette={chatPalette}
          getMessageVariant={getMessageVariant}
        />
      </Box>

      <Box>
        <ToggleButton
          value="multiline"
          selected={formatMode}
          onChange={() => setFormatMode((prev) => !prev)}
          size="small"
          color="info"
          sx={{ height: 24, py: 0, px: 1.5, mb: 1, borderRadius: '16px', textTransform: 'none', gap: 0.5 }}
        >
          <KeyboardReturnIcon sx={{ fontSize: '1rem' }} />
          {tGlobal('chat.multiline')}
        </ToggleButton>
        <ChatMessageInputBar
          ref={messageInputRef}
          onSendMessage={handleSendMessage}
          formatMode={formatMode}
          disabled={isCancelled}
          showTemplateHint={false}
        />
      </Box>
    </Container>
  )
}

export default memo(ChatPage)
